import React, { useMemo, useState } from "react"
import { Button, Input, Table } from "reactstrap"
import { formatearCuit, formatearNumero } from "helpers/formatters"
import BotonCopiarCuit from "components/BotonCopiarCuit"
import { normalizarId } from "./migracionEstadoHelpers"
import MaximizarTabla from "./MaximizarTabla"
import { motivoVisible } from "./motivoVisible"

// Historial de importaciones: tabla con buscador, orden por columna y
// paginacion para los listados de Creados / No importados. Se hizo aparte (sin
// components/Tables/DataTable.js) porque ese componente ofrece la opcion
// "Todos" y con ~17.000 filas congelaria el navegador; asi no se toca un
// componente compartido por el resto de la app. El orden y el filtro se
// resuelven en memoria sobre las filas que ya entrega el backend.

export const FILAS_POR_PAGINA = 25
export const VALOR_SIN_DATO = "__sin"

// Un unico collator: localeCompare con opciones crea uno por comparacion.
const COLLATOR = new Intl.Collator("es", { numeric: true })

const soloDigitos = valor => String(valor ?? "").replace(/\D/g, "")

const vacio = valor => valor == null || String(valor).trim() === ""

// Clave del select de filtro: id normalizado o VALOR_SIN_DATO si no hay id.
const claveFiltro = valor => {
  const id = normalizarId(valor)
  return id == null ? VALOR_SIN_DATO : String(id)
}

// Valor de un select de `filtros`: ids normalizados (sin ".0"), el resto como
// texto; vacio => VALOR_SIN_DATO.
const claveGenerica = valor => {
  if (vacio(valor)) return VALOR_SIN_DATO
  const id = normalizarId(valor)
  return id == null ? String(valor) : String(id)
}

// Valor de la celda tal como se MUESTRA: el motivo siempre saneado (defensa en
// profundidad: nunca nombres, host/URL ni CUIT, aunque la fila venga cruda).
// Lo usan la celda, el buscador y el orden: lo oculto no se indexa. Las siglas
// de entidad del catalogo (`columna.codigosEntidad`) se conservan en el
// segundo saneado, igual que al preparar las filas.
const valorVisible = (fila, columna) => {
  const valor = fila?.[columna.key]
  return columna.tipo === "motivo"
    ? motivoVisible(valor, columna.contexto, columna.codigosEntidad)
    : valor
}

// Texto que se muestra en la celda segun el tipo de columna.
const textoCelda = (fila, columna) => {
  const valor = valorVisible(fila, columna)
  if (columna.tipo === "id") {
    const id = normalizarId(valor)
    return id == null ? "-" : String(id)
  }
  if (vacio(valor)) return "-"
  if (columna.tipo === "cuit") return formatearCuit(valor)
  return String(valor)
}

// Contenido de la celda: `render(fila)` de la columna si existe; si no, el
// texto segun el tipo.
const contenidoCelda = (fila, columna) => {
  if (typeof columna.render === "function") return columna.render(fila)
  const texto = textoCelda(fila, columna)
  // BDSJEA-406: junto a cada CUIT, boton que copia el CUIT sin guiones.
  if (columna.tipo === "cuit" && !vacio(valorVisible(fila, columna))) {
    return (
      <>
        {texto}
        <BotonCopiarCuit cuit={valorVisible(fila, columna)} />
      </>
    )
  }
  return texto
}

// Valor comparable para ordenar.
const valorOrden = (fila, columna) => {
  // `valorOrden(fila)` de la columna: ordena por lo que se VE (p. ej. la
  // etiqueta de una observacion) y no por el valor crudo de la fila.
  const valor =
    typeof columna.valorOrden === "function"
      ? columna.valorOrden(fila)
      : valorVisible(fila, columna)
  if (columna.tipo === "id") return normalizarId(valor)
  if (vacio(valor)) return null
  if (columna.tipo === "cuit") return soloDigitos(valor)
  return String(valor).toLowerCase()
}

const comparar = (a, b, columna) => {
  // Los vacios van siempre al final, sin importar el sentido.
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1
  if (columna.tipo === "id") return a - b
  return COLLATOR.compare(String(a), String(b))
}

const coincideBusqueda = (fila, busqueda, columnasBusqueda) => {
  const texto = busqueda.trim().toLowerCase()
  if (!texto) return true
  // Busqueda por digitos solo si el texto es esencialmente numerico
  // (digitos, guiones y espacios): "abc123" no busca "123".
  const digitos = /^[\d\s-]+$/.test(texto) ? soloDigitos(texto) : ""
  return columnasBusqueda.some(columna => {
    const valor = valorVisible(fila, columna)
    if (vacio(valor)) return false
    if (columna.tipo === "cuit") {
      // Con o sin guiones: se compara por digitos.
      if (digitos) return soloDigitos(valor).includes(digitos)
      return String(valor).toLowerCase().includes(texto)
    }
    if (columna.tipo === "id") {
      const id = normalizarId(valor)
      return id != null && String(id).includes(texto)
    }
    return String(valor).toLowerCase().includes(texto)
  })
}

// Tabla con todo su estado (busqueda, filtros, orden, pagina). Cada instancia
// (la inline y la del modal "Maximizar") tiene el suyo.
const TablaInterna = ({
  columnas,
  filas,
  buscarEn,
  placeholderBuscador = "Buscar...",
  filtroSelect,
  filtros,
  testId,
  textoVacio,
  alturaMaxima,
  codigosEntidad,
}) => {
  const [busqueda, setBusqueda] = useState("")
  const [seleccion, setSeleccion] = useState("")
  const [valores, setValores] = useState({})
  const [orden, setOrden] = useState({ key: null, sentido: "asc" })
  const [pagina, setPagina] = useState(0)

  const cols = useMemo(
    () =>
      (Array.isArray(columnas) ? columnas.filter(Boolean) : []).map(c =>
        c.tipo === "motivo" && Array.isArray(codigosEntidad)
          ? { codigosEntidad, ...c }
          : c,
      ),
    [columnas, codigosEntidad],
  )
  const datos = useMemo(
    () =>
      Array.isArray(filas) ? filas.filter(f => f && typeof f === "object") : [],
    [filas],
  )
  const columnasBusqueda = useMemo(() => {
    const claves = Array.isArray(buscarEn) ? buscarEn : []
    return cols.filter(c => claves.includes(c.key))
  }, [cols, buscarEn])

  const listaFiltros = useMemo(
    () =>
      (Array.isArray(filtros) ? filtros : []).filter(
        f => f && typeof f.key === "string",
      ),
    [filtros],
  )

  const filtradas = useMemo(
    () =>
      datos.filter(fila => {
        for (const f of listaFiltros) {
          const elegido = valores[f.key]
          if (!elegido) continue
          // `predicado(fila, valorElegido)` permite filtrar por pertenencia
          // (p. ej. una fila con varias observaciones); sin el, igualdad.
          if (typeof f.predicado === "function") {
            if (!f.predicado(fila, elegido)) return false
          } else if (claveGenerica(fila[f.key]) !== elegido) {
            return false
          }
        }
        if (
          filtroSelect?.key &&
          seleccion &&
          claveFiltro(fila[filtroSelect.key]) !== seleccion
        ) {
          return false
        }
        return coincideBusqueda(fila, busqueda, columnasBusqueda)
      }),
    [
      datos,
      busqueda,
      seleccion,
      valores,
      listaFiltros,
      filtroSelect?.key,
      columnasBusqueda,
    ],
  )

  const ordenadas = useMemo(() => {
    const columna = cols.find(c => c.key === orden.key)
    if (!columna) return filtradas
    const signo = orden.sentido === "desc" ? -1 : 1
    return filtradas
      .map((fila, indice) => ({
        fila,
        indice,
        valor: valorOrden(fila, columna),
      }))
      .sort((x, y) => {
        // Clave precalculada una vez por fila (decorate-sort).
        const a = x.valor
        const b = y.valor
        // Vacios al final en ambos sentidos.
        if (a == null || b == null)
          return comparar(a, b, columna) || x.indice - y.indice
        return signo * comparar(a, b, columna) || x.indice - y.indice
      })
      .map(x => x.fila)
  }, [filtradas, cols, orden])

  const totalPaginas = Math.max(
    1,
    Math.ceil(ordenadas.length / FILAS_POR_PAGINA),
  )
  const paginaActual = Math.min(pagina, totalPaginas - 1)
  const desde = paginaActual * FILAS_POR_PAGINA
  const visibles = ordenadas.slice(desde, desde + FILAS_POR_PAGINA)

  const alternarOrden = key => {
    setOrden(previo =>
      previo.key === key
        ? { key, sentido: previo.sentido === "asc" ? "desc" : "asc" }
        : { key, sentido: "asc" },
    )
    setPagina(0)
  }

  const claseColumna = columna =>
    [
      columna.tipo === "id" ? "text-end text-nowrap pe-4" : "",
      columna.tipo === "cuit" ? "text-nowrap" : "",
      columna.tipo === "motivo" ? "tabla-importacion-motivo" : "",
    ]
      .filter(Boolean)
      .join(" ")

  const sentidoAria = columna => {
    if (orden.key !== columna.key) return "none"
    return orden.sentido === "asc" ? "ascending" : "descending"
  }

  const hayBuscador = columnasBusqueda.length > 0
  const hayFiltro = Boolean(filtroSelect?.key)
  const hayFiltros = listaFiltros.length > 0

  return (
    <div data-testid={testId}>
      {(hayBuscador || hayFiltro || hayFiltros) && (
        <div className="d-flex flex-wrap gap-2 mb-2">
          {hayBuscador && (
            <Input
              type="search"
              bsSize="sm"
              style={{ maxWidth: 280 }}
              value={busqueda}
              placeholder={placeholderBuscador}
              aria-label={placeholderBuscador}
              data-testid={`${testId}-buscador`}
              onChange={e => {
                setBusqueda(e.target.value)
                setPagina(0)
              }}
            />
          )}
          {hayFiltro && (
            <Input
              type="select"
              bsSize="sm"
              style={{ maxWidth: 220 }}
              value={seleccion}
              aria-label={filtroSelect.label || "Filtrar"}
              data-testid={`${testId}-filtro`}
              onChange={e => {
                setSeleccion(e.target.value)
                setPagina(0)
              }}
            >
              <option value="">{filtroSelect.label || "Todas"}</option>
              {(filtroSelect.opciones || []).map(op => (
                <option key={op.valor} value={op.valor}>
                  {op.etiqueta}
                </option>
              ))}
            </Input>
          )}
          {listaFiltros.map(f => (
            <Input
              key={f.key}
              type="select"
              bsSize="sm"
              style={{ maxWidth: 220 }}
              value={valores[f.key] || ""}
              aria-label={f.label || "Filtrar"}
              data-testid={`${testId}-filtro-${f.key}`}
              onChange={e => {
                const valor = e.target.value
                setValores(previo => ({ ...previo, [f.key]: valor }))
                setPagina(0)
              }}
            >
              <option value="">{f.todasLabel || f.label || "Todos"}</option>
              {(Array.isArray(f.opciones) ? f.opciones : []).map(op => (
                <option key={String(op.value)} value={String(op.value)}>
                  {op.label}
                </option>
              ))}
            </Input>
          ))}
        </div>
      )}

      {ordenadas.length === 0 ? (
        <p className="text-muted small mb-0" data-testid={`${testId}-vacio`}>
          {textoVacio}
        </p>
      ) : (
        <>
          <div
            style={
              alturaMaxima
                ? { maxHeight: alturaMaxima, overflowY: "auto" }
                : undefined
            }
          >
            <Table responsive hover size="sm" className="mb-1">
              <thead className="table-light">
                <tr>
                  {cols.map(columna => (
                    <th
                      key={columna.key}
                      className={claseColumna(columna)}
                      aria-sort={sentidoAria(columna)}
                      style={
                        columna.tipo === "motivo"
                          ? { minWidth: 280 }
                          : undefined
                      }
                    >
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-reset fw-bold text-decoration-none"
                        onClick={() => alternarOrden(columna.key)}
                      >
                        {columna.label}
                        {orden.key === columna.key && (
                          <i
                            className={`mdi ms-1 ${orden.sentido === "asc" ? "mdi-arrow-up" : "mdi-arrow-down"}`}
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibles.map((fila, i) => (
                  <tr key={desde + i}>
                    {cols.map(columna => (
                      <td key={columna.key} className={claseColumna(columna)}>
                        {contenidoCelda(fila, columna)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-1 small text-muted">
            <span data-testid={`${testId}-rango`}>
              {formatearNumero(desde + 1, 0)}-
              {formatearNumero(desde + visibles.length, 0)} de{" "}
              {formatearNumero(ordenadas.length, 0)}
            </span>
            <span className="d-flex align-items-center gap-2 text-nowrap">
              <Button
                size="sm"
                color="light"
                disabled={paginaActual === 0}
                onClick={() => setPagina(paginaActual - 1)}
              >
                Anterior
              </Button>
              <span data-testid={`${testId}-pagina`}>
                Página {formatearNumero(paginaActual + 1, 0)} de{" "}
                {formatearNumero(totalPaginas, 0)}
              </span>
              <Button
                size="sm"
                color="light"
                disabled={paginaActual >= totalPaginas - 1}
                onClick={() => setPagina(paginaActual + 1)}
              >
                Siguiente
              </Button>
            </span>
          </div>
        </>
      )}
    </div>
  )
}

/**
 * Props:
 *  - columnas: [{ key, label, tipo?: "id"|"cuit"|"texto"|"motivo"|"etiqueta",
 *      render?: fila => nodo, valorOrden?: fila => valor }]  (solo se
 *      renderizan las declaradas; `valorOrden` ordena por lo visible)
 *  - filas: [objeto]  (ya filtradas por el combo global, si lo hay)
 *  - buscarEn: [key]  (columnas del buscador; CUIT con o sin guiones)
 *  - placeholderBuscador, textoVacio, testId (default "tabla-importacion")
 *  - filtros: [{ key, label, todasLabel?, opciones: [{ value, label }] }]
 *      selects que se combinan (AND) entre si y con el buscador; con
 *      `predicado: (fila, valor) => boolean` el filtro es por pertenencia
 *  - filtroSelect: { key, label, opciones: [{ valor, etiqueta }] } (legado)
 *  - codigosEntidad: siglas de entidad del catalogo que las columnas de tipo
 *      "motivo" pueden mostrar (ademas de las conocidas)
 *  - titulo: texto para el boton/modal Maximizar
 *  - maximizable: boolean; agrega el boton "Maximizar tabla <titulo>"
 */
const TablaImportacion = ({
  testId = "tabla-importacion",
  textoVacio = "Sin datos.",
  titulo,
  maximizable = false,
  ...resto
}) => {
  const propsTabla = { ...resto, textoVacio }
  if (!maximizable) return <TablaInterna {...propsTabla} testId={testId} />
  return (
    <MaximizarTabla titulo={titulo} testId={testId}>
      {enModal =>
        enModal ? (
          <TablaInterna
            {...propsTabla}
            testId={`${testId}-max`}
            alturaMaxima="calc(100vh - 220px)"
          />
        ) : (
          <TablaInterna {...propsTabla} testId={testId} />
        )
      }
    </MaximizarTabla>
  )
}

export default TablaImportacion
